// src/components/GlobalUtils/DateHelpers.js
// Utilidades para manejo de fechas y horas

export const formatTimeRange = (startStr, endStr) => {
    const start = parseDateTimeFlexible(startStr);
    const end = parseDateTimeFlexible(endStr);
    if (!start || !end) return "";

    const opts = { hour: '2-digit', minute: '2-digit', hour12: true };
    const startT = start.toLocaleTimeString("es-MX", opts);
    const endT = end.toLocaleTimeString("es-MX", opts);
    return `${startT} - ${endT}`;
};

export const getLiteralHours = (value) => {
    if (!value || typeof value !== "string") return 0;
    const match = value.match(/(\d{2}):(\d{2})/);
    if (match) {
        return parseInt(match[1], 10) + parseInt(match[2], 10) / 60;
    }
    return 0;
};

export const getLiteralDateComponents = (value) => {
    if (!value || typeof value !== "string") return null;
    const match = value.match(/(\d{4})[-/](\d{2})[-/](\d{2})/);
    if (match) {
        return {
            year: parseInt(match[1], 10),
            month: parseInt(match[2], 10),
            day: parseInt(match[3], 10)
        };
    }
    return null;
};

const pad = (n) => String(n).padStart(2, "0");

/**
 * Devuelve la fecha/hora en formato YYYY-MM-DDTHH:MM
 * compatible con <input type="datetime-local" />
 */
export const toLocalDateTimeString = (date) => {
    if (!(date instanceof Date) || isNaN(date.getTime())) return "";
    const y = date.getFullYear();
    const m = pad(date.getMonth() + 1);
    const d = pad(date.getDate());
    const h = pad(date.getHours());
    const min = pad(date.getMinutes());
    return `${y}-${m}-${d}T${h}:${min}`;
};

/**
 * Devuelve la fecha/hora en formato YYYY-MM-DDTHH:MM:SS
 * (formato ISO local, evitando el desfase de toISOString que es UTC)
 */
export const toISODateTime = (date) => {
    if (!(date instanceof Date) || isNaN(date.getTime())) return "";
    const y = date.getFullYear();
    const m = pad(date.getMonth() + 1);
    const d = pad(date.getDate());
    const h = pad(date.getHours());
    const min = pad(date.getMinutes());
    const s = pad(date.getSeconds());
    return `${y}-${m}-${d}T${h}:${min}:${s}`;
};

/**
 * Primer día de la semana (domingo) para una fecha dada.
 */
export const getStartOfWeekCalendar = (date = new Date()) => {
    const d = new Date(date);
    const day = d.getDay(); // 0 = domingo
    const diff = d.getDate() - day;
    d.setHours(0, 0, 0, 0);
    d.setDate(diff);
    return d;
};

/**
 * Intenta parsear una fecha/hora que puede venir como:
 * - "YYYY-MM-DDTHH:MM"
 * - "YYYY-MM-DD HH:MM" (versiones anteriores)
 */
export const parseDateTimeFlexible = (value) => {
    if (!value || typeof value !== "string") return null;

    const normalized = value.trim();

    // Buscamos el patrón YYYY-MM-DD (o con /) seguido de T o espacio, y luego HH:mm
    // Eliminamos el ancla ^ por si hay caracteres invisibles o prefijos
    const match = normalized.match(/(\d{4})[-/](\d{2})[-/](\d{2})[T ](\d{2}):(\d{2})/);

    if (match) {
        const [_, year, month, day, hour, minute] = match.map(Number);
        // El constructor new Date(y, m-1, d, h, min) SIEMPRE usa la hora local del navegador,
        // ignorando cualquier cosa que venga después (segundos, Z, offset).
        const d = new Date(year, month - 1, day, hour, minute);
        if (!isNaN(d.getTime())) {
            return d;
        }
    }

    // Fallback: Si el patrón anterior falla, intentamos construirlo con el parser nativo,
    // pero si detectamos que el parser nativo lo tomó como UTC (contiene Z o T...Z),
    // intentaremos forzarlo a local si es posible.
    const fallback = new Date(normalized);
    if (!isNaN(fallback.getTime())) {
        return fallback;
    }

    return null;
};

/**
 * Devuelve la fecha/hora actual en formato YYYY-MM-DD HH:MM
 */
export const getNowLabel = () => toISODateTime(new Date());

/**
 * Devuelve la fecha/hora actual en formato YYYY-MM-DDTHH:MM
 */
export const getNowLocalDateTime = () => toLocalDateTimeString(new Date());
